import React, { useContext } from 'react';

import type { IAxiosResponseError } from 'api/types';
import {
  CheckboxFieldStateType,
  RadioFieldStateType,
  DropdownFieldStateType,
  InputFieldStateType,
  ISelectedFieldData, TextareaFieldStateType,

} from 'common/types/fields';
import { FallbackFormValues, StringKeys } from 'utils/type-tools';
import { IStorage } from './types';


/**
 * Async hook run before a fields update is dispatched. May return a replacement
 * sessionId (the layer swaps the session + cookie and sends the update there) or
 * null to keep the current session.
 */
export type BeforeFieldsUpdate = () => Promise<string | null>;

export interface IGenericFieldsHandlingLayer<FieldKeys extends PropertyKey, Field, FieldsChangeset extends Record<string, any>> {
  data: IStorage<FieldKeys, Field, IAxiosResponseError<unknown>>,
  session: {
    sessionId?: string,
  }
  actions: {
    updateFieldsState: (updatedFields: Array<FieldsChangeset>) => void,
    getFieldsByName: (fieldsNames: Array<FieldKeys>) => {[key in FieldKeys]? : Field },
    registerBeforeFieldsUpdate: (interceptor: BeforeFieldsUpdate | null) => void,
  },
}
export type PredefinedContextType<TFieldKey extends string = string> = IGenericFieldsHandlingLayer<
  TFieldKey,
  InputFieldStateType | TextareaFieldStateType | DropdownFieldStateType | CheckboxFieldStateType | RadioFieldStateType,
  ISelectedFieldData
>;

export const FieldsDataHandlerContext = React.createContext<
  PredefinedContextType
  | undefined
>(undefined);

/**
 * @name useGFHLContext - hook to take a FieldsDataHandlerContext. Store schema, state data, actions to update data
 * @type IGenericFieldsHandlingLayer | undefined
 */
export const useGFHLContext = <TFormValues extends FallbackFormValues = FallbackFormValues>() => {
  const context = useContext(FieldsDataHandlerContext) as PredefinedContextType<StringKeys<TFormValues>> | undefined;

  if (!context) {
    throw new Error('No FieldsDataHandlerContext found. This hook have to be used only inside appropriate context provider');
  }
  return context;
};
/**
 * @name useGenericFieldsData - hook to take a FieldsDataHandlerContext. Store schema, state data, actions to update data
 * @type IGenericFieldsHandlingLayer['data']
 */
export const useGenericFieldsData: <
    TFormValues extends FallbackFormValues = FallbackFormValues
>() => PredefinedContextType<StringKeys<TFormValues>>['data'] = <TFormValues extends FallbackFormValues = FallbackFormValues>() => {
  const context = useGFHLContext<TFormValues>();

  return context.data;
};
/**
 * @name useGenericFieldsData - hook to take a FieldsDataHandlerContext. Store schema, state data, actions to update data
 * @type IGenericFieldsHandlingLayer['actions']
 */
export const useGenericFieldsActions: <
    TFormValues extends FallbackFormValues = FallbackFormValues
>() => PredefinedContextType<StringKeys<TFormValues>>['actions'] = <TFormValues extends FallbackFormValues = FallbackFormValues>() => {
  const context = useGFHLContext<TFormValues>();

  return context.actions;
};
/**
 * @name useGenericFieldsData - hook to take a FieldsDataHandlerContext. Store schema, state data, actions to update data
 * @type IGenericFieldsHandlingLayer['actions']
 */
export const useSessionId: () => PredefinedContextType['session']['sessionId'] = () => {
  const context = useGFHLContext();

  return context.session.sessionId;
};
